import React from 'react';
import { ClientPopulated } from '@api/READ_ONLY/client_api/Api';
import AppAvatar from '@app/components/ui/Avatar';
import { Panel } from '@app/features/dashboard/components';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { useTranslation } from 'react-i18next';

import { formatRelativeDay, formatTime } from './clientCard.format';
import { buildCareTeam, cardDoctors, CardVisit, kinContact, TeamMember } from './clientCard.model';

const Row = ({ name, detail, tag }: { name: string; detail?: string; tag?: string }) => (
  <div className="flex items-center gap-2.5">
    <AppAvatar isSelected fullName={name} value={getInitials(name)} size={30} />
    <div className="min-w-0 flex-1">
      <div className="truncate text-[13px] font-medium text-slate-800">{name}</div>
      {detail && <div className="truncate text-[11px] text-slate-500">{detail}</div>}
    </div>
    {tag && (
      <span className="shrink-0 whitespace-nowrap rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
        {tag}
      </span>
    )}
  </div>
);

type CareTeamPanelProps = {
  client: ClientPopulated;
  visits: CardVisit[];
  className?: string;
};

/**
 * Who is around this client: the caregivers their visits name, every doctor they
 * currently have, and the contact to call.
 *
 * Nothing here is a stored team. It is what the visits, the client's doctors and
 * the client's contacts already say, gathered into the one place a coordinator
 * looks before phoning someone.
 */
const CareTeamPanel: React.FC<CareTeamPanelProps> = ({ client, visits, className }) => {
  const { t, i18n } = useTranslation();

  const relativeDayLabels = {
    today: t('client-card-today'),
    tomorrow: t('client-card-tomorrow'),
    yesterday: t('client-card-yesterday'),
  };

  const team = buildCareTeam(visits);
  const kin = kinContact(client);
  const doctors = cardDoctors(client);

  const memberDetail = (member: TeamMember): string | undefined => {
    if (member.next) {
      return t('client-card-team-next', {
        when: `${formatRelativeDay(member.next.startTime, i18n.language, relativeDayLabels)} ${formatTime(
          member.next.startTime,
          i18n.language
        )}`,
      });
    }
    if (member.last) {
      return t('client-card-team-last', {
        when: formatRelativeDay(member.last.startTime, i18n.language, relativeDayLabels),
      });
    }
    return undefined;
  };

  const hasAnyone = team.current || team.upcoming.length > 0 || team.previous.length > 0 || doctors.length > 0 || kin;

  return (
    <Panel title={t('client-card-care-team')} className={className}>
      {!hasAnyone ? (
        <div className="text-[13px] text-slate-400">{t('client-card-care-team-empty')}</div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {team.current && (
            <Row name={team.current.name} detail={memberDetail(team.current)} tag={t('client-card-team-current')} />
          )}

          {team.upcoming.length > 0 && (
            <>
              <div className="border-t border-slate-100 pt-2.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                {t('client-card-team-upcoming')}
              </div>
              {team.upcoming.map((member) => (
                <Row key={member.caregiverId} name={member.name} detail={memberDetail(member)} />
              ))}
            </>
          )}

          {team.previous.length > 0 && (
            <>
              <div className="border-t border-slate-100 pt-2.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                {t('client-card-team-previous')}
              </div>
              {team.previous.map((member) => (
                <Row
                  // By id, not name: two caregivers can share one.
                  key={member.caregiverId}
                  name={member.name}
                  // Silent at 0 rather than "0 visits": a caregiver whose past visits
                  // were never marked completed has still been here, and the date tag
                  // says when. See TeamMember.completed.
                  detail={
                    member.completed > 0 ? t('client-card-team-visit-count', { count: member.completed }) : undefined
                  }
                  tag={
                    member.last ? formatRelativeDay(member.last.startTime, i18n.language, relativeDayLabels) : undefined
                  }
                />
              ))}
            </>
          )}

          {doctors.length > 0 && (
            // Headed like the caregiver groups, because until now the doctor was a row
            // under a plain rule and read as one more caregiver. A coordinator scanning
            // for who to phone needs to know which of these people is which.
            <>
              <div className="border-t border-slate-100 pt-2.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                {t('client-card-team-doctors')}
              </div>
              {doctors.map((doctor) => (
                <Row
                  // The relationship's id, not the doctor's: it is what the row is.
                  key={doctor.clientDoctorId}
                  name={`${doctor.doctorFirstName ?? ''} ${doctor.doctorLastName ?? ''}`.trim()}
                  detail={
                    [doctor.doctorSpecialization, doctor.doctorPhoneMobile].filter(Boolean).join(' · ') || undefined
                  }
                  // No tag rather than a guessed one: a relationship with no type on it
                  // is neither attending nor referring, and saying either invents a fact.
                  tag={
                    doctor.doctorType === 'ATTENDING'
                      ? t('client-card-doctor-attending')
                      : doctor.doctorType === 'REFERRING'
                        ? t('client-card-doctor-referring')
                        : undefined
                  }
                />
              ))}
            </>
          )}

          {kin && (
            <div className="border-t border-slate-100 pt-2.5 text-[12px] leading-relaxed text-slate-600">
              <span className="text-slate-400">{t('client-card-kin')}: </span>
              {`${kin.firstName} ${kin.lastName}`.trim()}
              {kin.relationship ? ` (${kin.relationship})` : ''}
              {kin.phoneMobile || kin.phoneHome ? ` · ${kin.phoneMobile ?? kin.phoneHome}` : ''}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
};

export default CareTeamPanel;
